import { motion, AnimatePresence } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { X, AlertTriangle } from "lucide-react";
import { itemLabel } from "./accountsModel";

const Overlay = ({ onClose, children, className, testId }) => (
  <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50" onClick={onClose}>
    <motion.div initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.95, opacity: 0 }} className={`glass-card p-6 rounded-xl w-full mx-4 ${className}`} onClick={(e) => e.stopPropagation()} data-testid={testId}>
      {children}
    </motion.div>
  </motion.div>
);

const FIELDS = {
  accounts: [
    { key: "companyName", label: "Company Name *", placeholder: "Enter company name", testId: "add-company-name" },
    { key: "industry", label: "Industry", placeholder: "e.g. Technology, Healthcare", testId: "add-industry" },
    { key: "persona", label: "Target Persona", placeholder: "e.g. CTO, VP Engineering", testId: "add-persona" },
  ],
  leads: [
    { key: "companyName", label: "Company Name *", placeholder: "Enter company name", testId: "add-company-name" },
    { key: "contactName", label: "Contact Name", placeholder: "Enter contact name", testId: "add-contact-name" },
    { key: "contactTitle", label: "Contact Title", placeholder: "e.g. VP of Sales", testId: "add-contact-title" },
    { key: "contactEmail", label: "Email", placeholder: "email@company.com", testId: "add-email", type: "email" },
    { key: "industry", label: "Industry", placeholder: "e.g. Technology, Healthcare", testId: "add-industry" },
  ],
};

export const AddItemModal = ({ form, viewMode, submitting, setForm, onClose, onSubmit }) => (
  <AnimatePresence>
    {form && (
      <Overlay onClose={onClose} className="max-w-md" testId="add-modal">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl text-white">Add New {itemLabel(viewMode)}</h2>
          <Button variant="ghost" size="sm" onClick={onClose} className="text-gray-400 hover:text-white" data-testid="add-modal-close"><X className="h-5 w-5" /></Button>
        </div>
        <div className="space-y-4">
          {FIELDS[viewMode].map(({ key, label, placeholder, testId, type }) => (
            <div key={key}>
              <label className="text-sm text-gray-400 block mb-1">{label}</label>
              <Input type={type || "text"} value={form[key] || ""} onChange={(e) => setForm((prev) => ({ ...prev, [key]: e.target.value }))} placeholder={placeholder} className="bg-white/5 border-white/10 text-white" data-testid={testId} />
            </div>
          ))}
        </div>
        <div className="flex gap-3 mt-6">
          <Button variant="outline" onClick={onClose} className="flex-1 border-white/20 text-gray-300">Cancel</Button>
          <Button onClick={onSubmit} disabled={!form.companyName || submitting} className="flex-1 bg-ism-teal hover:bg-ism-teal/90 text-white" data-testid="submit-add-btn">
            {submitting ? "Adding..." : `Add ${itemLabel(viewMode)}`}
          </Button>
        </div>
      </Overlay>
    )}
  </AnimatePresence>
);

export const DeleteConfirmModal = ({ item, viewMode, submitting, onClose, onConfirm }) => (
  <AnimatePresence>
    {item && (
      <Overlay onClose={onClose} className="max-w-sm" testId="delete-confirm-modal">
        <div className="text-center">
          <div className="w-12 h-12 rounded-full bg-red-500/20 flex items-center justify-center mx-auto mb-4">
            <AlertTriangle className="h-6 w-6 text-red-400" />
          </div>
          <h3 className="text-lg text-white mb-2">Delete {itemLabel(viewMode)}?</h3>
          <p className="text-gray-400 text-sm mb-6">
            Are you sure you want to delete <span className="text-white">{item.companyName}</span>? This action cannot be undone.
          </p>
          <div className="flex gap-3">
            <Button variant="outline" onClick={onClose} className="flex-1 border-white/20 text-gray-300">Cancel</Button>
            <Button onClick={onConfirm} disabled={submitting} className="flex-1 bg-red-500 hover:bg-red-600 text-white" data-testid="confirm-delete-btn">
              {submitting ? "Deleting..." : "Delete"}
            </Button>
          </div>
        </div>
      </Overlay>
    )}
  </AnimatePresence>
);
